import { ControllerType } from '@/common/types/student';
import { getStudent } from '@/game/GameComponents/PhaserGame/GameInstance';
import { StudentGameClickToPlaceInputHandler } from '@/game/GameComponents/PhaserGame/Input/StudentGameClickToPlaceInputHandler';
import { StudentGameDragNDropInputHandler } from '@/game/GameComponents/PhaserGame/Input/StudentGameDragNDropInputHandler';
import { StudentGameKeyboardInputHandler } from '@/game/GameComponents/PhaserGame/Input/StudentGameKeyboardInputHandler';
import EventsManager from '@/game/GameComponents/PhaserGame/Managers/EventsManager';
import StudentGame from '@/game/GameComponents/PhaserGame/StudentGames/StudentGame';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';
import StudentGameCard from '@/game/prefabs/StudentGame/StudentGameCard';

export class StudentGameInputHandler {
  constructor(private studentGame: StudentGame) {
    // Create both input systems. Switch between them based on student settings.
    this.keyboardInputSystem = new StudentGameKeyboardInputHandler(
      this.studentGame,
    );
    this.dragNDropInputSystem = new StudentGameDragNDropInputHandler(
      this.studentGame,
    );
    this.clickToPlaceInputSystem = new StudentGameClickToPlaceInputHandler(
      this.studentGame,
    );

    const student = getStudent();
    if (!student) {
      console.error('No student found');
      return;
    }
    this.updateCurrentInputSystem(
      studentGame.getControlScheme(student.settings.controllerType),
    );
    EventsManager.registerEventOn(
      this.studentGame,
      GAME_EVENTS.INPUT_METHOD_CHANGED,
      this.handleInputMethodChanged,
      this,
    );
    EventsManager.registerEventOn(
      this.studentGame,
      GAME_EVENTS.STUDENT_GAME_ROUND_STARTED,
      this.handleRoundStarted,
      this,
    );
    EventsManager.registerEventOn(
      this.studentGame,
      GAME_EVENTS.MINIGAME_CHECK_ANSWER,
      this.handleAnswerSubmitted,
      this,
    );
  }

  keyboardInputSystem?: StudentGameKeyboardInputHandler;
  dragNDropInputSystem?: StudentGameDragNDropInputHandler;
  clickToPlaceInputSystem?: StudentGameClickToPlaceInputHandler;
  feedbackDelayedCall?: Phaser.Time.TimerEvent;

  currInputSystem?:
    | StudentGameDragNDropInputHandler
    | StudentGameKeyboardInputHandler
    | StudentGameClickToPlaceInputHandler;

  updateCurrentInputSystem(newDesiredControllerType: ControllerType) {
    const controllerType = this.studentGame.getControlScheme(
      newDesiredControllerType,
    );
    this.currInputSystem =
      this.getInputSystemFromControllerType(controllerType);
  }

  getInputSystemFromControllerType(controllerType: ControllerType) {
    switch (controllerType) {
      case 'keyboard':
        return this.keyboardInputSystem;
      case 'drag':
        return this.dragNDropInputSystem;
      case 'click':
        return this.clickToPlaceInputSystem;
    }
  }

  isUsingCards(controllerType: ControllerType | undefined): boolean {
    if (controllerType === undefined) return false;
    return [ControllerType.CLICK, ControllerType.DRAG].includes(controllerType);
  }

  isUsingCardsFromInputHandler() {
    return (
      this.currInputSystem === this.clickToPlaceInputSystem ||
      this.currInputSystem === this.dragNDropInputSystem
    );
  }

  handleInputMethodChanged(newDesiredControllerType: ControllerType) {
    this.updateCurrentInputSystem(newDesiredControllerType);
    if (this.isUsingCards(newDesiredControllerType)) {
      this.resetCardGuidance();
    }
  }

  // We can initialize the card input here because the player
  // could switch input methods mid-game.
  initializeCardInput(card: StudentGameCard) {
    card.onStartedDragging = (
      studentGameCard: StudentGameCard,
      pointer: any,
      _dragX: number,
      _dragY: number,
    ) => {
      if (this.currInputSystem === this.dragNDropInputSystem) {
        this.dragNDropInputSystem?.handleStartedCardDragging(
          pointer,
          studentGameCard,
        );
      }
      this.clearCardGuidance();
    };
    card.onEndedDragging = (
      studentGameCard: StudentGameCard,
      pointer: any,
      dragX: number,
      dragY: number,
    ) => {
      if (this.currInputSystem === this.dragNDropInputSystem) {
        this.dragNDropInputSystem?.handleCardDrop(
          studentGameCard,
          pointer,
          dragX,
          dragY,
        );
      }
    };
    card.onDragged = (pointer: any, dragX: number, dragY: number) => {
      if (this.currInputSystem === this.dragNDropInputSystem) {
        this.dragNDropInputSystem?.handleCardDragged(
          card,
          pointer,
          dragX,
          dragY,
        );
      }
      this.clearCardGuidance();
    };
    card.onClicked = (studentGameCard: StudentGameCard) => {
      if (this.currInputSystem === this.clickToPlaceInputSystem) {
        this.clickToPlaceInputSystem?.handleCardClicked(studentGameCard);
      }
      this.clearCardGuidance();
    };
  }

  resetCardGuidance() {
    const time = this.studentGame.scene.scene.time;
    this.clearCardGuidance();
    if (this.isUsingCardsFromInputHandler()) {
      if (this.studentGame.shouldShowCardGuidance()) {
        this.feedbackDelayedCall = time.delayedCall(5000, () => {
          this.studentGame
            .getHandCards()
            .forEach((card: StudentGameCard) => card.showGuidance());
        });
      }
    }
  }

  clearCardGuidance() {
    const time = this.studentGame.scene.scene.time;
    this.studentGame
      .getHandCards()
      .forEach(
        (card: StudentGameCard, _index: number, _array: StudentGameCard[]) =>
          card.clearOutline(),
      );
    if (this.feedbackDelayedCall) {
      time.removeEvent(this.feedbackDelayedCall);
    }
  }

  async handleRoundStarted() {
    this.resetCardGuidance();
  }

  handleAnswerSubmitted() {
    this.clearCardGuidance();
  }
}
